(() => {
  const SELECTOR = '[data-watch-wrist-simulator]';

  class WatchWristSimulator {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-wrist-simulator-dialog]');
      this.openButton = root.querySelector('[data-wrist-simulator-open]');
      this.closeButtons = root.querySelectorAll('[data-wrist-simulator-close]');
      this.stage = root.querySelector('[data-wrist-simulator-stage]');
      this.watchLayer = root.querySelector('[data-watch-layer]');
      this.watchImage = root.querySelector('[data-watch-image]');
      this.wristInput = root.querySelector('[data-wrist-size]');
      this.scaleInput = root.querySelector('[data-watch-scale]');
      this.rotationInput = root.querySelector('[data-watch-rotation]');
      this.wristValue = root.querySelector('[data-wrist-value]');
      this.scaleValue = root.querySelector('[data-scale-value]');
      this.rotationValue = root.querySelector('[data-rotation-value]');
      this.measurement = root.querySelector('[data-case-measurement]');
      this.photoInput = root.querySelector('[data-wrist-photo-input]');
      this.photo = root.querySelector('[data-wrist-photo]');
      this.defaultWrist = root.querySelector('[data-default-wrist]');
      this.resetPhotoButton = root.querySelector('[data-reset-wrist-photo]');
      this.resetButton = root.querySelector('[data-wrist-simulator-reset]');
      this.variantMap = this.parseVariantMap();

      this.caseDiameterMm = this.toNumber(root.dataset.caseDiameter, 43);
      this.caseRatio = this.toNumber(root.dataset.caseRatio, 0.49);
      this.currentVariantId = String(root.dataset.selectedVariantId || '');
      this.position = { x: 0, y: 0 };
      this.rotation = 0;
      this.fineScale = 1;
      this.drag = null;
      this.lastFocusedElement = null;
      this.objectUrl = null;
      this.resizeObserver = null;
      this.formObserver = null;

      this.bindEvents();
      this.placeTrigger();
      this.syncFromCurrentVariant();
      this.observeVariantChanges();
      this.observeStage();
      this.update();
    }

    parseVariantMap() {
      const node = this.root.querySelector('[data-wrist-variant-map]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.warn('Watch wrist simulator: invalid variant map.', error);
        return {};
      }
    }

    toNumber(value, fallback) {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    bindEvents() {
      this.openButton?.addEventListener('click', () => this.open());
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.wristInput?.addEventListener('input', () => this.update());
      this.scaleInput?.addEventListener('input', () => this.update());
      this.rotationInput?.addEventListener('input', () => this.update());

      this.resetButton?.addEventListener('click', () => this.resetPosition());
      this.photoInput?.addEventListener('change', (event) => this.loadPhoto(event));
      this.resetPhotoButton?.addEventListener('click', () => this.removePhoto());

      this.stage?.addEventListener('pointerdown', (event) => this.startDrag(event));
      window.addEventListener('pointermove', (event) => this.moveDrag(event));
      window.addEventListener('pointerup', () => this.endDrag());
      window.addEventListener('pointercancel', () => this.endDrag());

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.dialog && !this.dialog.hidden) {
          this.close();
        }

        if (event.key === 'Tab' && this.dialog && !this.dialog.hidden) {
          this.trapFocus(event);
        }
      });

      [
        'variant:change',
        'product:variant-change',
        'variant-change',
        'product:change'
      ].forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          const variant =
            event.detail?.variant ||
            event.detail?.selectedVariant ||
            event.detail?.productVariant;

          if (variant?.id) {
            this.setVariant(String(variant.id), variant);
          } else {
            window.setTimeout(() => this.syncFromCurrentVariant(), 0);
          }
        });
      });

      document.addEventListener('change', (event) => {
        if (!event.target.closest('form[action*="/cart/add"], product-form, [data-product-form]')) return;
        window.setTimeout(() => this.syncFromCurrentVariant(), 0);
      });
    }

    placeTrigger() {
      const productScope =
        this.root.closest('.shopify-section') ||
        document.querySelector('main') ||
        document.body;

      const candidates = [
        '[data-product-media]',
        '.product-gallery__media',
        '.product-gallery',
        '.product__media-wrapper',
        '.product-media',
        '.product__media',
        'media-gallery'
      ];

      let host = null;
      for (const selector of candidates) {
        host = productScope.querySelector(selector);
        if (host) break;
      }

      if (!host || host.contains(this.openButton)) return;

      const computedPosition = window.getComputedStyle(host).position;
      if (computedPosition === 'static') {
        host.style.position = 'relative';
      }
      host.appendChild(this.openButton);
    }

    open() {
      if (!this.dialog) return;
      this.lastFocusedElement = document.activeElement;
      this.dialog.hidden = false;
      document.body.classList.add('wrist-simulator-open');
      this.syncFromCurrentVariant();

      requestAnimationFrame(() => {
        this.update();
        this.dialog.querySelector('.wrist-simulator__close')?.focus();
      });
    }

    close() {
      if (!this.dialog) return;
      this.dialog.hidden = true;
      document.body.classList.remove('wrist-simulator-open');
      this.lastFocusedElement?.focus?.();
    }

    trapFocus(event) {
      const focusable = [...this.dialog.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    observeStage() {
      if (!window.ResizeObserver || !this.stage) return;
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.stage);
    }

    observeVariantChanges() {
      const idInputs = document.querySelectorAll(
        'form[action*="/cart/add"] input[name="id"], product-form input[name="id"], [data-product-form] input[name="id"]'
      );

      idInputs.forEach((input) => {
        const observer = new MutationObserver(() => this.syncFromCurrentVariant());
        observer.observe(input, {
          attributes: true,
          attributeFilter: ['value']
        });
      });
    }

    getCurrentVariantId() {
      const checkedVariant =
        document.querySelector('form[action*="/cart/add"] input[name="id"]:checked') ||
        document.querySelector('product-form input[name="id"]:checked') ||
        document.querySelector('[data-product-form] input[name="id"]:checked');

      if (checkedVariant?.value) return String(checkedVariant.value);

      const idInput =
        document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
        document.querySelector('product-form input[name="id"]') ||
        document.querySelector('[data-product-form] input[name="id"]');

      if (idInput?.value) return String(idInput.value);

      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      return urlVariant || this.currentVariantId;
    }

    syncFromCurrentVariant() {
      const variantId = this.getCurrentVariantId();
      if (variantId) this.setVariant(variantId);
    }

    setVariant(variantId, eventVariant = null) {
      this.currentVariantId = String(variantId);
      const mapped = this.variantMap[this.currentVariantId];

      const eventImage =
        eventVariant?.featured_media?.preview_image?.src ||
        eventVariant?.featured_image?.src ||
        eventVariant?.image?.src ||
        eventVariant?.image;

      const image = eventImage || mapped?.image || this.root.dataset.defaultImage;
      const ratio = this.toNumber(mapped?.caseRatio, this.toNumber(this.root.dataset.caseRatio, 0.49));

      if (image && this.watchImage) {
        const normalizedImage = this.normalizeImageUrl(image);
        if (this.watchImage.src !== normalizedImage) {
          this.watchImage.src = normalizedImage;
        }
      }

      this.caseRatio = ratio;
      this.update();
    }

    normalizeImageUrl(url) {
      if (!url) return '';
      if (url.startsWith('//')) return `${window.location.protocol}${url}`;
      try {
        return new URL(url, window.location.origin).href;
      } catch (_) {
        return url;
      }
    }

    update() {
      if (!this.stage || !this.watchLayer) return;

      const wristCm = this.toNumber(this.wristInput?.value, 17);
      const wristCircumferenceMm = wristCm * 10;
      const wristWidthMm = wristCircumferenceMm / 2.45;

      const stageWidth = this.stage.clientWidth || 500;

      /*
        The default illustrated wrist occupies about 54% of the canvas width.
        For an uploaded wrist photo, 54% remains a reasonable starting estimate,
        while the fine adjustment slider lets the customer compensate for framing.
      */
      const visualWristWidthPx = stageWidth * 0.54;
      const caseDiameterPx = (this.caseDiameterMm / wristWidthMm) * visualWristWidthPx;

      this.fineScale = this.toNumber(this.scaleInput?.value, 100) / 100;
      this.rotation = this.toNumber(this.rotationInput?.value, 0);

      const safeRatio = Math.min(Math.max(this.caseRatio, 0.15), 0.95);
      const fullImageWidthPx = (caseDiameterPx / safeRatio) * this.fineScale;

      this.watchLayer.style.width = `${fullImageWidthPx}px`;
      this.watchLayer.style.transform =
        `translate(-50%, -50%) translate(${this.position.x}px, ${this.position.y}px) rotate(${this.rotation}deg)`;

      if (this.wristValue) this.wristValue.textContent = wristCm.toFixed(1);
      if (this.scaleValue) this.scaleValue.textContent = String(Math.round(this.fineScale * 100));
      if (this.rotationValue) this.rotationValue.textContent = String(Math.round(this.rotation));
      if (this.measurement) this.measurement.textContent = `${this.caseDiameterMm} mm`;
    }

    startDrag(event) {
      if (!this.watchLayer || !event.target.closest('[data-watch-layer]')) return;
      event.preventDefault();

      this.drag = {
        startX: event.clientX,
        startY: event.clientY,
        originX: this.position.x,
        originY: this.position.y
      };

      this.watchLayer.classList.add('is-dragging');
      this.stage.setPointerCapture?.(event.pointerId);
    }

    moveDrag(event) {
      if (!this.drag) return;
      this.position.x = this.drag.originX + (event.clientX - this.drag.startX);
      this.position.y = this.drag.originY + (event.clientY - this.drag.startY);
      this.update();
    }

    endDrag() {
      this.drag = null;
      this.watchLayer?.classList.remove('is-dragging');
    }

    resetPosition() {
      this.position = { x: 0, y: 0 };
      if (this.scaleInput) this.scaleInput.value = '100';
      if (this.rotationInput) this.rotationInput.value = '0';
      this.update();
    }

    loadPhoto(event) {
      const file = event.target.files?.[0];
      if (!file || !file.type.startsWith('image/')) return;

      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = URL.createObjectURL(file);

      this.photo.src = this.objectUrl;
      this.photo.hidden = false;
      this.defaultWrist.hidden = true;
      this.resetPhotoButton.hidden = false;
    }

    removePhoto() {
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = null;
      }

      this.photo.removeAttribute('src');
      this.photo.hidden = true;
      this.defaultWrist.hidden = false;
      this.resetPhotoButton.hidden = true;
      if (this.photoInput) this.photoInput.value = '';
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.wristSimulatorInitialized === 'true') return;
      root.dataset.wristSimulatorInitialized = 'true';
      new WatchWristSimulator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
